/** Every decision the timeline makes before it draws anything.
 *
 *  Pure: no React, no DOM, no fetch. Day boundaries, reserved heights and the
 *  heart-rate scale are arithmetic, and arithmetic is the part a test can pin.
 *  `Timeline.tsx` and `Bubble.tsx` are then thin enough to read in one sitting.
 *
 *  The load-bearing one is estimateSize. The virtualizer asks for a row's height
 *  BEFORE that row exists, and corrects itself afterwards by measuring. Every
 *  pixel it guesses wrong is a pixel the list jumps under the reader's thumb
 *  mid-scroll -- which is the specific jank this whole archive was built to
 *  avoid. So a media row's height comes from the file's own stored w/h, never
 *  from a default, and never from waiting for the image to load.
 */
import { F, localDate, mediaKind, type Row } from "./types";

// ------------------------------------------------------------------- layout
// One place for the numbers, because estimateSize and the CSS have to agree:
// if they drift, every row is mis-estimated and the correction is the jank.
export const LINE_H = 20;
export const BUBBLE_PAD_Y = 16;   // 8 top + 8 bottom
export const BUBBLE_PAD_X = 24;   // 12 each side
export const GAP = 2;             // between bubbles inside one run
export const RUN_GAP = 10;        // extra breathing room after a run ends
export const FOOTER_H = 16;       // the time - sender line, only at a run's end
export const DAY_H = 44;          // the inline day separator
export const GAP_H = 34;          // the inline "2h 40m" separator
export const AUDIO_H = 56;        // fixed: a note must never resize the list
export const MEDIA_INSET = 8;     // the photo's margin inside its bubble

/** Below this, a silence is not worth a line of its own.
 *
 *  One number, deliberately. Too low and every busy evening is a ladder of
 *  chips; too high and the three-hour gaps that are the point of the feature
 *  read the same as a quick reply. 30 minutes -- tune it here. */
export const GAP_MIN_S = 30 * 60;

/** 15px system sans, averaged over the example's 83 bodies. Deliberately a
 *  little narrow: over-estimating a line is a gap, under-estimating is a jump. */
const CHAR_W = 7.6;

/** Wide enough to read, narrow enough that you can still see whose side it is
 *  on. Capped so the archive does not turn into full-width paragraphs on a
 *  desktop browser. */
export const bubbleMax = (vw: number) => Math.min(vw * 0.78, 420);

/** A photo may not eat the whole screen: you need to see the rows around it to
 *  know where you are in three years of scrolling. */
const mediaMaxH = (vh: number) => vh * 0.62;

// --------------------------------------------------------------------- days
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** 'Sat 1 Jun 2024'.
 *
 *  Every read is getUTC*, and that is not fussiness. `new Date('2024-06-01')`
 *  parses as UTC MIDNIGHT, so a reader west of Greenwich formatting it in their
 *  own zone sees 31 May -- every day header in the archive off by one, for some
 *  readers and not others. Reading back in UTC makes the bug unexpressible.
 *  Hand-rolled rather than Intl for the same reason: no locale, no ICU version,
 *  no browser deciding this should say 6/1/2024.
 */
export function dayLabel(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  return `${DAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** 'Jun 2024'.
 *
 *  No Date object anywhere: 'YYYY-MM' already IS the two numbers, and building
 *  a Date to read them back is precisely how dayLabel's UTC trap gets in. Same
 *  hand-rolled month names for the same reason -- no Intl, no locale, nothing
 *  that can decide this should say 06/2024.
 */
export function monthLabel(ym: string): string {
  const m = Number(ym.slice(5, 7));
  return `${MONTHS[m - 1] ?? "?"} ${ym.slice(0, 4)}`;
}

export const isDayStart = (rows: Row[], i: number): boolean =>
  i === 0 || localDate(rows[i]) !== localDate(rows[i - 1]);

// --------------------------------------------------------------------- gaps
/** A row's wall-clock time in seconds.
 *
 *  Sliced and handed to Date.UTC, NEVER `new Date(tsLocal)`. A
 *  space-separated datetime with no zone is implementation-defined, and V8
 *  reads it as the READER's local time -- so a phone in London and a phone in
 *  Bishkek would compute different gaps from identical data. Date.UTC takes
 *  numbers and cannot be talked into a timezone.
 *
 *  This is a wall CLOCK, not an instant. That is exactly what a gap wants --
 *  the chip should say what the clock on the wall said -- and it is why
 *  gapBefore only ever subtracts two rows from the same local day.
 */
export function tsSeconds(r: Row): number {
  const s = r[F.TsLocal];
  return Date.UTC(
    Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, Number(s.slice(8, 10)),
    Number(s.slice(11, 13)), Number(s.slice(14, 16)), Number(s.slice(17, 19)),
  ) / 1000;
}

/** How long nobody said anything before row i -- 0 if that is not worth saying.
 *
 *  Zero on a day start, because the day rule already carries that fact with a
 *  date attached. Zero on a negative delta, which same-day rows really can
 *  produce: the index is ordered by ts_utc, and a zone change inside one day
 *  moves the local strings without moving the order.
 *
 *  Same-day only, so the wall-clock subtraction is honest -- with the one
 *  exception of a DST transition INSIDE a day, where this reads an hour out.
 *  The index ships no UTC, so there is nothing here to compute the truth from;
 *  it costs one wrong chip twice a year, and rows.test.ts pins it.
 */
export function gapBefore(rows: Row[], i: number): number {
  if (i <= 0 || isDayStart(rows, i)) return 0;
  const d = tsSeconds(rows[i]) - tsSeconds(rows[i - 1]);
  return d > GAP_MIN_S ? d : 0;
}

/** '31m', '2h 40m', '5h'. Minutes are rounded FIRST, so 59.6 minutes carries to
 *  '1h' rather than rendering as the impossible '0h 60m'. */
export function gapLabel(seconds: number): string {
  const total = Math.round(seconds / 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** The last row of a run from one sender on one day -- the only row that gets a
 *  'time - sender' footer. A run of ten messages captioned ten times is noise.
 *
 *  A gap also ends a run, and that is the point of it: it puts a clock time on
 *  BOTH sides of the silence, so the chip says how long and the footers say
 *  when. Without it one timestamp covers three hours and reads as one moment.
 */
export function isRunEnd(rows: Row[], i: number): boolean {
  const r = rows[i];
  if (!r[F.Sender]) return true;           // an orphan is always alone
  const next = rows[i + 1];
  if (!next) return true;                  // must precede the gap check below
  if (gapBefore(rows, i + 1) > 0) return true;
  return next[F.Sender] !== r[F.Sender] || localDate(next) !== localDate(r);
}

// -------------------------------------------------------------------- media
export const thumbSrc = (r: Row) =>
  r[F.MediaId] === null ? null : `/media/thumb/${r[F.MediaId]}`;
export const fullSrc = (r: Row) =>
  r[F.MediaId] === null ? null : `/media/full/${r[F.MediaId]}`;

/** The reserved box for a photo or a video poster, in CSS pixels, known before
 *  a single byte of the image has been fetched. Null for audio and for the 18
 *  MEDIA messages in the full export whose file never matched. */
export function mediaBox(r: Row, vw: number, vh: number): { width: number; height: number } | null {
  const kind = mediaKind(r);
  if (kind === null || kind === "audio") return null;
  const width = Math.round(bubbleMax(vw) - MEDIA_INSET);
  const w = r[F.W];
  const h = r[F.H];
  // Snapchat is a portrait camera; 3:4 is the right guess when ffprobe gave us
  // nothing. What it must never be is 0, which collapses the slot entirely.
  const ratio = w && h && w > 0 && h > 0 ? h / w : 4 / 3;
  return { width, height: Math.min(Math.round(width * ratio), Math.round(mediaMaxH(vh))) };
}

/** hashOrPeaks is two different things in one field. A peaks array starts with
 *  '[', which is a perfectly valid base83 character, so decoding one as a
 *  blurhash yields a plausible and completely wrong colour. Ask through here. */
export const blurhashOf = (r: Row): string | null =>
  mediaKind(r) === "audio" ? null : r[F.HashOrPeaks];

/** 64 normalised floats, or null. Null covers a corrupt row, and one corrupt
 *  row should cost one waveform -- not the whole archive. */
export function parsePeaks(r: Row): number[] | null {
  if (mediaKind(r) !== "audio") return null;
  const raw = r[F.HashOrPeaks];
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    if (!parsed.every((v) => typeof v === "number" && Number.isFinite(v))) return null;
    return parsed as number[];
  } catch {
    return null;
  }
}

export const formatDuration = (s: number | null): string =>
  s === null || !Number.isFinite(s) || s < 0
    ? ""
    : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

// -------------------------------------------------------------------- colour
const B83 = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz#$%*+,-.:;=?@[]^_{|}~";
const NEUTRAL = "#2a2a30";

/** The blurhash's average colour, from its DC term alone -- characters 2..6.
 *  25 nanoseconds, against 0.16 ms for a full decode, so this can paint on the
 *  very first frame and the decoded gradient can arrive whenever it likes.
 *  There is then never a blank box, which is what a fast flick would show. */
export function avgColor(hash: string | null): string {
  if (!hash || hash.length < 6) return NEUTRAL;
  let v = 0;
  for (let i = 2; i < 6; i++) {
    const d = B83.indexOf(hash[i]);
    if (d < 0) return NEUTRAL;
    v = v * 83 + d;
  }
  const hex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${hex((v >> 16) & 255)}${hex((v >> 8) & 255)}${hex(v & 255)}`;
}

/** Dim rose at rest, vivid when it climbed.
 *
 *  Anchored at 60-110 because that is where the data lives: across the 74
 *  badged messages, p10 is 69 and p90 is 100. Scaling to the full 40-195 the
 *  watches can report would render every message the same pink and throw the
 *  feature away. Null returns null -- no colour, no heart, no placeholder.
 */
export function bpmColor(bpm: number | null): string | null {
  if (bpm === null || !Number.isFinite(bpm)) return null;
  const t = Math.max(0, Math.min(1, (bpm - 60) / 50));
  return `hsl(350, ${Math.round(30 + 50 * t)}%, ${Math.round(45 + 18 * t)}%)`;
}

/** How far the reading was from the row: 'now', '+3m', '\u22122m'.
 *
 *  Empty for no reading, so the caller renders nothing rather than a dash.
 *  Under 30 seconds is 'now' -- the watch samples on its own schedule and the
 *  message timestamp is to the second, so '+0m' would claim a precision this
 *  does not have. The extremes are exactly the ingest window's own bounds
 *  ([t-120, t+900] in ingest/common.py), which is what makes the range visible
 *  per row instead of promised once in a comment nobody scrolling will read.
 */
export function lagLabel(lagS: number | null): string {
  if (lagS === null || !Number.isFinite(lagS)) return "";
  if (Math.abs(lagS) < 30) return "now";
  return `${lagS < 0 ? "\u2212" : "+"}${Math.round(Math.abs(lagS) / 60)}m`;
}

// --------------------------------------------------------------------- text
/** What a row with no body is. Media names itself; an unsaved message has to
 *  explain itself, because it is not an empty row and not a parsing failure --
 *  Snapchat deleted the words before the export was made, and unsaved is the
 *  DEFAULT for every chat. The row still carries a true time and a true heart
 *  rate, so it belongs on the timeline; it just cannot say what was said. */
export function placeholder(r: Row): string {
  const kind = mediaKind(r);
  if (kind === "audio") return "voice note";
  if (kind === "video") return "video";
  if (kind === "image") return "photo";
  if (r[F.Kind] === "TEXT") return "unsaved — not kept by Snapchat";
  // A MEDIA message with no file: 18 of these in the full export. The chat HTML
  // never says whether a snap was a photo, a video or a voice note -- only
  // ffprobe on the file knows, and there is no file. "photo" would be a guess.
  if (r[F.Kind] === "MEDIA") return "snap — no file in the export";
  return r[F.Kind].toLowerCase();
}

const textHeight = (r: Row, vw: number): number => {
  const perLine = Math.max(1, Math.floor((bubbleMax(vw) - BUBBLE_PAD_X) / CHAR_W));
  const text = r[F.Body] ?? placeholder(r);
  return Math.max(1, Math.ceil(text.length / perLine)) * LINE_H;
};

// ------------------------------------------------------------------- height
/** What the virtualizer reserves for row i, before row i exists. */
export function estimateSize(rows: Row[], i: number, vw: number, vh: number): number {
  const r = rows[i];
  if (!r) return LINE_H;
  let h = GAP + BUBBLE_PAD_Y;
  // Never both: gapBefore is already 0 on a day start.
  if (isDayStart(rows, i)) h += DAY_H;
  else if (gapBefore(rows, i)) h += GAP_H;
  if (isRunEnd(rows, i)) h += FOOTER_H + RUN_GAP;

  const box = mediaBox(r, vw, vh);
  if (box) h += box.height;
  else if (mediaKind(r) === "audio") h += AUDIO_H;
  else h += textHeight(r, vw);
  return h;
}

// -------------------------------------------------------------------- scroll
/** The topmost row actually on screen. getVirtualItems() begins `overscan` rows
 *  ABOVE the fold, so items[0] would label the day chip with a day you have
 *  already scrolled past. */
export function topVisible<T extends { end: number }>(items: T[], offset: number): T | null {
  for (const item of items) if (item.end > offset) return item;
  return items.length ? items[items.length - 1] : null;
}

/** The row indices the viewer can open, in timeline order.
 *
 *  Photos and videos only. A voice note has a media file but nothing to look
 *  at, and stepping through the archive's pictures should not land you on a
 *  black screen with a waveform on it.
 */
export function viewableMedia(rows: Row[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < rows.length; i++) {
    const kind = mediaKind(rows[i]);
    if (kind === "image" || kind === "video") out.push(i);
  }
  return out;
}
